import React, { useState, useEffect } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Select,
  Row,
  Col,
  Typography,
  Divider,
  Alert,
  Table,
  Tag,
  Space,
  Modal,
  Tooltip,
  Switch,
  message,
  Tabs,
  Collapse,
  Statistic,
} from "antd";
import {
  LinkOutlined,
  CopyOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined,
  WhatsAppOutlined,
  GlobalOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import api from "../../shared/api.jsx";

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const PageContainer = styled.div`
  padding: 24px;
  background: #f5f5f5;
  min-height: 100vh;
`;

const GeneratorCard = styled(Card)`
  margin-bottom: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const UrlDisplayCard = styled(Card)`
  margin-top: 16px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
`;

const UrlField = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px;
  background: white;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  margin-bottom: 8px;

  .url-text {
    flex: 1;
    font-family: "Courier New", monospace;
    font-size: 12px;
    word-break: break-all;
    color: #1890ff;
  }

  .copy-button {
    flex-shrink: 0;
  }
`;

const TemplateCard = styled(Card)`
  cursor: pointer;
  transition: all 0.3s;
  border: 2px solid transparent;

  &:hover {
    border-color: #1890ff;
    transform: translateY(-2px);
  }

  &.selected {
    border-color: #1890ff;
    background: #f0f8ff;
  }
`;

const CampaignUrlGeneratorPage = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState({});
  const [generatedUrls, setGeneratedUrls] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [activeTab, setActiveTab] = useState("generator");
  const [saveToDatabase, setSaveToDatabase] = useState(true);

  // Default templates
  const defaultTemplates = {
    facebook: {
      name: "Facebook Ads",
      description: "For Facebook and Instagram advertising campaigns",
      defaultParams: {
        utm_source: "facebook",
        utm_medium: "cpc",
        utm_content: "video_ad",
      },
    },
    google: {
      name: "Google Ads",
      description: "For Google Search and Display campaigns",
      defaultParams: {
        utm_source: "google",
        utm_medium: "cpc",
        utm_content: "search_ad",
      },
    },
    email: {
      name: "Email Marketing",
      description: "For email newsletter campaigns",
      defaultParams: {
        utm_source: "email",
        utm_medium: "email",
        utm_content: "newsletter",
      },
    },
    organic: {
      name: "Organic Social",
      description: "For organic social media posts",
      defaultParams: {
        utm_source: "social",
        utm_medium: "organic",
        utm_content: "post",
      },
    },
    referral: {
      name: "Referral/Partnership",
      description: "For partner and referral campaigns",
      defaultParams: {
        utm_source: "referral",
        utm_medium: "referral",
        utm_content: "partner",
      },
    },
  };

  useEffect(() => {
    setTemplates(defaultTemplates);
    fetchCampaigns();
  }, []);

  const fetchCampaigns = async () => {
    try {
      const response = await api.get("/campaign-urls/campaigns");
      if (response.success) {
        setCampaigns(response.data);
      }
    } catch (error) {
      console.error("Error fetching campaigns:", error);
      // Set empty array on error
      setCampaigns([]);
    }
  };

  const handleGenerateUrl = async (values) => {
    setLoading(true);
    try {
      const response = await api.post("/campaign-urls/generate", {
        ...values,
        saveToDatabase,
      });

      if (response.success) {
        setGeneratedUrls(response.data);
        message.success("Campaign URL generated successfully!");
        form.resetFields();
        fetchCampaigns();
      } else {
        message.error(response.error || "Failed to generate URL");
      }
    } catch (error) {
      console.error("Error generating URL:", error);
      message.error("Failed to generate campaign URL");
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateFromTemplate = async (values) => {
    if (!selectedTemplate) {
      message.error("Please select a template first");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/campaign-urls/generate-from-template", {
        template: selectedTemplate,
        campaignName: values.campaignName,
        utmCampaign: values.utmCampaign,
        customParams: values.customParams || {},
        saveToDatabase,
      });

      if (response.success) {
        setGeneratedUrls(response.data);
        message.success("Campaign URL generated from template!");
        form.resetFields();
        setSelectedTemplate(null);
        fetchCampaigns();
      } else {
        message.error(response.error || "Failed to generate URL");
      }
    } catch (error) {
      console.error("Error generating from template:", error);
      message.error("Failed to generate campaign URL");
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      message.success("URL copied to clipboard!");
    });
  };

  const handleDeleteCampaign = async (campaignId) => {
    Modal.confirm({
      title: "Delete Campaign",
      content:
        "Are you sure you want to delete this campaign? This action cannot be undone.",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          const response = await api.delete(
            `/campaign-urls/campaigns/${campaignId}`
          );
          if (response.success) {
            message.success("Campaign deleted successfully");
            fetchCampaigns();
          } else {
            message.error(response.error || "Failed to delete campaign");
          }
        } catch (error) {
          console.error("Error deleting campaign:", error);
          message.error("Failed to delete campaign");
        }
      },
    });
  };

  const campaignColumns = [
    {
      title: "Campaign Name",
      dataIndex: "name",
      key: "name",
      render: (text) => <Text strong>{text}</Text>,
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      render: (source) => <Tag color="blue">{source}</Tag>,
    },
    {
      title: "Medium",
      dataIndex: "medium",
      key: "medium",
      render: (medium) => <Tag color="green">{medium}</Tag>,
    },
    {
      title: "Campaign",
      dataIndex: "campaign",
      key: "campaign",
      render: (text) => <Text code>{text}</Text>,
    },
    {
      title: "Performance",
      key: "performance",
      render: (_, record) => (
        <div>
          <div>Leads: {record.metrics?.leadsCreated || 0}</div>
          <div>Orders: {record.metrics?.ordersCreated || 0}</div>
        </div>
      ),
    },
    {
      title: "Last Updated",
      dataIndex: "lastUpdated",
      key: "lastUpdated",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => handleViewCampaign(record.id)}
            />
          </Tooltip>
          <Tooltip title="Delete Campaign">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDeleteCampaign(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleViewCampaign = async (campaignId) => {
    try {
      const response = await api.get(`/campaign-urls/campaigns/${campaignId}`);
      if (response.success) {
        Modal.info({
          title: `Campaign Details: ${response.data.name}`,
          width: 600,
          content: (
            <div>
              <Row gutter={16}>
                <Col span={12}>
                  <Statistic
                    title="Total Leads"
                    value={response.data.metrics?.leadsCreated || 0}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Total Orders"
                    value={response.data.metrics?.ordersCreated || 0}
                  />
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col span={12}>
                  <Statistic
                    title="Conversion Rate"
                    value={
                      response.data.conversionRates?.overallConversion || 0
                    }
                    suffix="%"
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Total Revenue"
                    value={response.data.totalRevenue || 0}
                    prefix="R"
                  />
                </Col>
              </Row>
              <Divider />
              <Text strong>UTM Parameters:</Text>
              <div style={{ marginTop: 8 }}>
                <Text code>utm_source: {response.data.source}</Text>
                <br />
                <Text code>utm_medium: {response.data.medium}</Text>
                <br />
                <Text code>utm_campaign: {response.data.campaign}</Text>
              </div>
            </div>
          ),
        });
      }
    } catch (error) {
      console.error("Error fetching campaign details:", error);
      message.error("Failed to fetch campaign details");
    }
  };

  const tabItems = [
    {
      key: "generator",
      label: "URL Generator",
      children: (
        <Row gutter={24}>
          <Col xs={24} lg={16}>
            <GeneratorCard title="Generate Campaign URL">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleGenerateUrl}
                initialValues={{
                  utmSource: "facebook",
                  utmMedium: "cpc",
                  utmContent: "general",
                  baseUrl: "https://bubakii.co.za",
                  whatsappNumber:
                    import.meta.env.VITE_CONTACT_PHONE || "27xxxxxxxxx",
                }}
              >
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="campaignName"
                      label="Campaign Name"
                      rules={[
                        {
                          required: true,
                          message: "Campaign name is required",
                        },
                      ]}
                    >
                      <Input placeholder="e.g., Share Load Q1 2024" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item
                      name="utmCampaign"
                      label="UTM Campaign"
                      rules={[
                        { required: true, message: "UTM campaign is required" },
                      ]}
                    >
                      <Input placeholder="e.g., share_load_q1_2024" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={8}>
                    <Form.Item name="utmSource" label="UTM Source">
                      <Select>
                        <Option value="facebook">Facebook</Option>
                        <Option value="google">Google</Option>
                        <Option value="instagram">Instagram</Option>
                        <Option value="email">Email</Option>
                        <Option value="social">Social</Option>
                        <Option value="referral">Referral</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="utmMedium" label="UTM Medium">
                      <Select>
                        <Option value="cpc">CPC (Paid Search)</Option>
                        <Option value="cpm">CPM (Display)</Option>
                        <Option value="email">Email</Option>
                        <Option value="organic">Organic</Option>
                        <Option value="social">Social</Option>
                        <Option value="referral">Referral</Option>
                      </Select>
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={8}>
                    <Form.Item name="utmContent" label="UTM Content">
                      <Input placeholder="e.g., video_ad, banner, newsletter" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name="utmTerm" label="UTM Term (Keywords)">
                      <Input placeholder="e.g., furniture_moving, relocation" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="whatsappNumber" label="WhatsApp Number">
                      <Input placeholder="e.g., 27xxxxxxxxx" />
                    </Form.Item>
                  </Col>
                </Row>

                <Collapse
                  ghost
                  items={[
                    {
                      key: "advanced",
                      label: "Advanced Settings",
                      children: (
                        <Row gutter={16}>
                          <Col xs={24} md={12}>
                            <Form.Item name="baseUrl" label="Base URL">
                              <Input placeholder="https://your-domain.com" />
                            </Form.Item>
                          </Col>
                          <Col xs={24} md={12}>
                            <Form.Item label="Save to Database">
                              <Switch
                                checked={saveToDatabase}
                                onChange={setSaveToDatabase}
                              />
                              <Text style={{ marginLeft: 8 }}>
                                {saveToDatabase ? "Enabled" : "Disabled"}
                              </Text>
                            </Form.Item>
                          </Col>
                        </Row>
                      ),
                    },
                  ]}
                />

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    icon={<PlusOutlined />}
                    size="large"
                  >
                    Generate Campaign URL
                  </Button>
                </Form.Item>
              </Form>

              {generatedUrls && (
                <UrlDisplayCard title="Generated URLs">
                  <Alert
                    message="Campaign URL Generated Successfully!"
                    description={`Campaign ID: ${generatedUrls.campaignId}`}
                    type="success"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <div>
                    <Text strong>Campaign URL:</Text>
                    <UrlField>
                      <span className="url-text">
                        {generatedUrls.urls.campaign}
                      </span>
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() =>
                          copyToClipboard(generatedUrls.urls.campaign)
                        }
                        className="copy-button"
                      />
                    </UrlField>

                    <Text strong>WhatsApp URL:</Text>
                    <UrlField>
                      <span className="url-text">
                        {generatedUrls.urls.whatsapp}
                      </span>
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() =>
                          copyToClipboard(generatedUrls.urls.whatsapp)
                        }
                        className="copy-button"
                      />
                    </UrlField>

                    <Text strong>Tracking URL:</Text>
                    <UrlField>
                      <span className="url-text">
                        {generatedUrls.urls.tracking}
                      </span>
                      <Button
                        type="text"
                        icon={<CopyOutlined />}
                        onClick={() =>
                          copyToClipboard(generatedUrls.urls.tracking)
                        }
                        className="copy-button"
                      />
                    </UrlField>
                  </div>
                </UrlDisplayCard>
              )}
            </GeneratorCard>
          </Col>

          <Col xs={24} lg={8}>
            <GeneratorCard title="Quick Templates">
              <Text type="secondary">
                Select a template to quickly generate campaign URLs with
                pre-configured settings.
              </Text>

              <div style={{ marginTop: 16 }}>
                {Object.entries(templates).map(([key, template]) => (
                  <TemplateCard
                    key={key}
                    size="small"
                    className={selectedTemplate === key ? "selected" : ""}
                    onClick={() => setSelectedTemplate(key)}
                    style={{ marginBottom: 8 }}
                  >
                    <div>
                      <Text strong>{template.name}</Text>
                      <br />
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {template.description}
                      </Text>
                    </div>
                  </TemplateCard>
                ))}
              </div>

              {selectedTemplate && (
                <div style={{ marginTop: 16 }}>
                  <Form layout="vertical" onFinish={handleGenerateFromTemplate}>
                    <Form.Item
                      name="campaignName"
                      label="Campaign Name"
                      rules={[
                        {
                          required: true,
                          message: "Campaign name is required",
                        },
                      ]}
                    >
                      <Input placeholder="Enter campaign name" />
                    </Form.Item>

                    <Form.Item
                      name="utmCampaign"
                      label="UTM Campaign"
                      rules={[
                        { required: true, message: "UTM campaign is required" },
                      ]}
                    >
                      <Input placeholder="Enter UTM campaign" />
                    </Form.Item>

                    <Form.Item>
                      <Button
                        type="primary"
                        htmlType="submit"
                        loading={loading}
                        block
                      >
                        Generate from Template
                      </Button>
                    </Form.Item>
                  </Form>
                </div>
              )}
            </GeneratorCard>
          </Col>
        </Row>
      ),
    },
    {
      key: "management",
      label: "Campaign Management",
      children: (
        <GeneratorCard title="Existing Campaigns">
          <Table
            columns={campaignColumns}
            dataSource={campaigns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            loading={loading}
          />
        </GeneratorCard>
      ),
    },
    {
      key: "help",
      label: "Help & Documentation",
      children: (
        <GeneratorCard title="How to Use Campaign URLs">
          <Collapse
            defaultActiveKey={["1"]}
            items={[
              {
                key: "1",
                label: "What are UTM Parameters?",
                children: (
                  <>
                    <Paragraph>
                      UTM parameters are tags you add to URLs to track the
                      effectiveness of your marketing campaigns. They help you
                      identify which campaigns are driving traffic and
                      conversions.
                    </Paragraph>
                    <ul>
                      <li>
                        <Text code>utm_source</Text> - The source of traffic
                        (facebook, google, email)
                      </li>
                      <li>
                        <Text code>utm_medium</Text> - The marketing medium
                        (cpc, email, social)
                      </li>
                      <li>
                        <Text code>utm_campaign</Text> - The specific campaign
                        name
                      </li>
                      <li>
                        <Text code>utm_content</Text> - The content variant
                        (video_ad, banner)
                      </li>
                      <li>
                        <Text code>utm_term</Text> - Keywords for paid search
                        campaigns
                      </li>
                    </ul>
                  </>
                ),
              },
              {
                key: "2",
                label: "Best Practices",
                children: (
                  <ul>
                    <li>
                      Use consistent naming conventions for your campaigns
                    </li>
                    <li>Keep UTM parameters short and descriptive</li>
                    <li>Use lowercase letters and underscores</li>
                    <li>Test your URLs before launching campaigns</li>
                    <li>Monitor campaign performance regularly</li>
                  </ul>
                ),
              },
              {
                key: "3",
                label: "URL Examples",
                children: (
                  <>
                    <Text strong>Facebook Ads:</Text>
                    <Text code block>
                      https://bubakii.co.za?utm_source=facebook&utm_medium=cpc&utm_campaign=share_load_q1&utm_content=video_ad
                    </Text>

                    <Text strong>Google Ads:</Text>
                    <Text code block>
                      https://bubakii.co.za?utm_source=google&utm_medium=cpc&utm_campaign=furniture_moving&utm_content=search_ad&utm_term=relocation
                    </Text>

                    <Text strong>Email Newsletter:</Text>
                    <Text code block>
                      https://bubakii.co.za?utm_source=email&utm_medium=email&utm_campaign=newsletter_jan&utm_content=newsletter
                    </Text>
                  </>
                ),
              },
            ]}
          />
        </GeneratorCard>
      ),
    },
  ];

  return (
    <PageContainer>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          marginBottom: "16px",
        }}
      >
        <div>
          <Title level={2}>
            <LinkOutlined /> Campaign URL Generator
          </Title>
          <Paragraph>
            Generate tracking URLs for your marketing campaigns with UTM
            parameters. Track performance from ad clicks to conversions.
          </Paragraph>
        </div>
        <Button
          type="default"
          icon={<ReloadOutlined />}
          onClick={() => window.location.reload()}
          title="Refresh Page"
        >
          Refresh
        </Button>
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
    </PageContainer>
  );
};

export default CampaignUrlGeneratorPage;
